import React, { useState, useEffect } from 'react';
import { Routes, Route, useMatch } from 'react-router-dom';
import AnalysisList from './AnalysisList';
import Report from './Report';
import SleepParameterDetail from './SleepParameterDetail';
import Hypnogram from './Hypnogram';
import Epoch from './Epoch';
import MicroAnnotation from './MicroAnnotation';
import NewAnnotation from './NewAnnotation';
import ChatWidget from '../components/ChatWidget';
import ProcessingView from './ProcessingView';
import API_URL from '../config';

const Analysis = ({ renderBanner }) => {
  const match = useMatch('/analysis/:recordingId/*');
  const recordingId = match?.params?.recordingId;
  const [patientInfo, setPatientInfo] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState(null);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState("");

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      try {
        const recordingResponse = await fetch(`${API_URL}/api/recording/${recordingId}/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (!recordingResponse.ok) throw new Error('Failed to fetch recording data');
        const recordingData = await recordingResponse.json();

        setRecordingStatus(recordingData.status);
        setRecordingProgress(recordingData.processing_progress || 0);
        setProcessingMessage(recordingData.processing_stage_name || "Starting...");

        const patientResponse = await fetch(`${API_URL}/api/patient/${recordingData.patient}/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (!patientResponse.ok) throw new Error('Failed to fetch patient data');
        const patientData = await patientResponse.json();
        
        setPatientInfo({
          ...patientData,
          recordingDate: recordingData.recording_date
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        if (!intervalId) setPatientInfo(null);
      }
    };

    if (recordingId) {
      fetchData();
      
      // Setup polling if we know it's processing or just started
      intervalId = setInterval(async () => {
        // Fetch recording status only on interval to check progress
        try {
          const recRes = await fetch(`${API_URL}/api/recording/${recordingId}/`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          const recData = await recRes.json();
          setRecordingStatus(recData.status);
          setRecordingProgress(recData.processing_progress || 0);
          setProcessingMessage(recData.processing_stage_name || "");
          
          if (recData.status === 'completed' || recData.status === 'failed') {
            clearInterval(intervalId);
          }
        } catch (e) {
             console.error("Polling error", e);
        }
      }, 3000);
      
      return () => clearInterval(intervalId);
    } else {
      setPatientInfo(null);
      setRecordingStatus(null);
    }
  }, [recordingId]);

  if (renderBanner) {
    if (!recordingId) {
      return renderBanner({
         name: '',
         id: 'Select a recording below',
         recordingDate: null
      });
    }
    return renderBanner(patientInfo);
  }

  if (recordingStatus === 'failed') {
    return (
      <div className="flex-1 flex flex-col min-h-full bg-white dark:bg-gray-900 items-center justify-center p-8">
         <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-6 rounded-lg text-center max-w-lg border border-red-200 dark:border-red-800">
             <h3 className="text-xl font-bold mb-2">Processing Failed</h3>
             <p className="text-sm">There was an error while processing the EDF file. Please try re-uploading the file or contact support.</p>
         </div>
         <ChatWidget />
      </div>
    );
  }

  const isActiveProcessing = recordingStatus === 'processing' || recordingStatus === 'queued';

  return (
    <div className="flex flex-col min-h-full bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
      <div className="flex-1">
        {isActiveProcessing ? (
          <ProcessingView 
            recordingId={recordingId} 
            progress={recordingProgress}
            message={processingMessage}
            patientInfo={patientInfo} 
            setPatientInfo={setPatientInfo}
            status={recordingStatus}
          />
        ) : (
          <Routes>
            <Route index element={<AnalysisList />} />
            <Route path=":recordingId/report" element={<Report />} />
            <Route path=":recordingId/parameter-detail" element={<SleepParameterDetail />} />
            <Route path=":recordingId/hypnogram" element={<Hypnogram />} />
            <Route path=":recordingId/epoch/:epochId" element={<Epoch />} />
            <Route path=":recordingId/epoch/:epochId/annotation/new" element={<NewAnnotation />} />
            <Route path=":recordingId/epoch/:epochId/annotation/:annotationType/:start/:end/:channel" element={<MicroAnnotation />} />
            <Route path=":recordingId/epoch/:epochId/annotation/:annotationType/:start/:end" element={<MicroAnnotation />} />
          </Routes>
        )}
      </div>
      <ChatWidget />
    </div>
  );
};

export default Analysis;